import type { RateCardOption } from 'v5/core/entity-schema/entitySchema.types';
import type { ListAddAction } from 'v5/core/form-schema/formSchema.types';
import { rateCardFormValue } from 'v5/core/entity-values/codecs/rate.codec';
import {
  describeRate,
  rateSummaryLine,
  type RateDescriptionCopy
} from 'v5/core/rates/describeRate';
import type { ResourceSchemaCopy } from './resourceSchemaCopy';

/**
 * The rates list's add action: "Add rate" adds a blank custom rate, "From
 * rate card" adds a row linked to a card (option id `card:<id>`). The
 * resolver builds it; the list binding parses the picked id back into a
 * card id.
 */

const RATE_CARD_OPTION_PREFIX = 'card:';

export function rateCardOptionId(cardId: number): string {
  return `${RATE_CARD_OPTION_PREFIX}${cardId}`;
}

/** `card:<id>` → id; anything else → `null`. */
export function parseRateCardOptionId(optionId: string): number | null {
  if (!optionId.startsWith(RATE_CARD_OPTION_PREFIX)) return null;
  const raw = optionId.slice(RATE_CARD_OPTION_PREFIX.length);
  if (!/^\d+$/.test(raw)) return null;
  return Number(raw);
}

/** "GBP 1,200 / day · +20% agency", the way the rate list shows the card once added. */
export function describeRateCard(
  card: RateCardOption,
  copy: RateDescriptionCopy
): string {
  return rateSummaryLine(describeRate(rateCardFormValue(card), copy), copy);
}

/** The card picker beside the button, or a single button when there are no cards and no settings to open. */
export function rateListAddAction(
  rateCards: ReadonlyArray<RateCardOption>,
  copy: ResourceSchemaCopy,
  /** The rate cards settings, for those who may configure them; `null` otherwise. */
  settingsPath: string | null = null
): ListAddAction {
  // No cards to pick, and nobody to send to the settings: a plain button.
  if (rateCards.length === 0 && settingsPath === null) {
    return { type: 'single', label: copy.addRate };
  }
  return {
    type: 'single-or-template',
    label: copy.addRate,
    blank: { label: copy.blankRate, description: copy.blankRateHint },
    templates: {
      label: copy.fromRateCard,
      options: rateCards.map(card => ({
        id: rateCardOptionId(card.id),
        label: card.name,
        description: describeRateCard(card, copy.rateCard)
      }))
    },
    manage:
      settingsPath === null
        ? null
        : { label: copy.manageRateCards, href: settingsPath }
  };
}
